import Typography from "components/typography";
import React, { useRef, useState } from "react";
import styled from "styled-components";
import { Colors } from "utils/colors";
import { ReactComponent as Available } from "images/svg/available.svg";
import html2canvas from "html2canvas";
import Spinner from "components/spinner";

interface IProps {
  cardImage: string;
  cardName: string;
  trackImage?: string;
  variant?: boolean;
}
const PromoCard = ({ cardImage, cardName, trackImage, variant }: IProps) => {
  const designRef = useRef<HTMLDivElement>(null);
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownload = async () => {
    if (designRef.current) {
      setDownloadLoading(true);
      // Preload images
      const images = designRef.current.getElementsByTagName("img");
      const imagePromises = Array.from(images).map((img) => {
        if (img.complete) return Promise.resolve();
        return new Promise((resolve) => {
          img.onload = resolve;
          img.onerror = resolve;
        });
      });

      // Wait for all images to load
      await Promise.all(imagePromises);

      const canvas = await html2canvas(designRef.current, {
        backgroundColor: null,
        scale: 10,
        useCORS: true,
        allowTaint: true,
        logging: false
      });

      const link = document.createElement("a");
      link.href = canvas.toDataURL("image/png");
      link.download = `${cardName.replace(/\s+/g, "_")}.png`;
      link.click();
    }
    setDownloadLoading(false);
  };
  return (
    <Container>
      <DesignContainer ref={designRef}>
        {trackImage && (
          <>
            <TrackImage src={trackImage} alt="track cover" />
            <ImageFooter>
              <Available color={variant ? "black" : "white"} />
            </ImageFooter>
          </>
        )}
        <CardImage src={cardImage} alt={cardName} />
      </DesignContainer>
      <Flex>
        <Typography size="m" color={Colors.white300} weight="medium">
          {cardName}
        </Typography>
        {trackImage &&
          (downloadLoading ? (
            <Spinner />
          ) : (
            <DownloadBtn onClick={handleDownload}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
              >
                <path
                  d="M2 10C2 11.8856 2 12.8284 2.58579 13.4142C3.17157 14 4.11438 14 6 14H10C11.8856 14 12.8284 14 13.4142 13.4142C14 12.8284 14 11.8856 14 10"
                  stroke="#CDCDCD"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
                <path
                  d="M7.9987 2V10.6667M7.9987 10.6667L10.6654 7.75M7.9987 10.6667L5.33203 7.75"
                  stroke="#CDCDCD"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </DownloadBtn>
          ))}
      </Flex>
    </Container>
  );
};

export default PromoCard;

const Container = styled.div`
  width: 100%;
  height: auto;
  border-radius: 0.8rem;
  padding: 1rem 1.1rem;
  background-color: ${Colors.grey700};
`;

const DesignContainer = styled.div`
  width: 100%;
  border-radius: 0.6rem;
  overflow: hidden;
  margin-bottom: 1rem;
  position: relative;
`;

const CardImage = styled.img`
  width: 100%;
  height: 100%;
  position: relative;
`;
const TrackImage = styled.img`
  position: absolute;
  z-index: 1;
  top: 1.6rem;
  width: 70%;
  left: 50%;
  transform: translateX(-50%);
`;

const ImageFooter = styled.div`
  position: absolute;
  z-index: 1;
  bottom: 2rem;
  width: 40%;
  left: 50%;
  transform: translateX(-50%);
`;
const Flex = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DownloadBtn = styled.div`
  cursor: pointer;
`;
